import { useCallback, useEffect, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { useAws } from "@/context/AwsContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  GraduationCap, Ticket, CheckCircle2, XCircle, Lightbulb, ShieldCheck, ChevronRight,
  X, Trophy, ArrowRight, Minimize2, ListChecks,
} from "lucide-react";

const DIFF = { Beginner: "text-emerald-500", Intermediate: "text-amber-500", Engineer: "text-rose-500" };

function renderRich(text) {
  return String(text).split(/(\*\*.+?\*\*)/g).map((part, i) =>
    part.startsWith("**") && part.endsWith("**")
      ? <strong key={i} className="font-semibold text-white">{part.slice(2, -2)}</strong>
      : <span key={i}>{part}</span>);
}

export default function AwsGuidedPanel({ open, initialScenario, onClose, setView }) {
  const { refresh } = useAws();
  const [scenarios, setScenarios] = useState([]);
  const [sid, setSid] = useState(initialScenario || null);
  const [scn, setScn] = useState(null);
  const [prog, setProg] = useState(null);
  const [result, setResult] = useState(null);
  const [validating, setValidating] = useState(false);

  const loadList = useCallback(async () => {
    const { data } = await api.get("/aws/guided/scenarios");
    setScenarios(data.scenarios);
  }, []);

  const startScenario = useCallback(async (id) => {
    const { data } = await api.post(`/aws/guided/scenarios/${id}/start`);
    setScn(data.scenario); setProg(data.progress); setSid(id); setResult(null);
    await refresh();
  }, [refresh]);

  useEffect(() => { if (open) loadList(); }, [open, loadList]);
  useEffect(() => { if (open && initialScenario) startScenario(initialScenario); }, [open, initialScenario, startScenario]);

  if (!open) return null;

  const taskStatus = (tid) => prog?.tasks?.[tid]?.status || "locked";
  const activeIdx = scn ? scn.tasks.findIndex((t) => taskStatus(t.id) === "active") : -1;
  const curIdx = activeIdx >= 0 ? activeIdx : (scn ? scn.tasks.length : 0);
  const task = scn ? scn.tasks[curIdx] || null : null;
  const completed = prog?.status === "completed";
  const nextRec = [...scenarios]
    .filter((s) => s.id !== sid && s.status !== "completed")
    .sort((a, b) => a.order - b.order)[0] || null;

  const validate = async () => {
    if (!task) return;
    setValidating(true); setResult(null);
    try {
      const { data } = await api.post(`/aws/guided/scenarios/${sid}/tasks/${task.id}/validate`);
      setResult(data);
      const r = await api.get(`/aws/guided/scenarios/${sid}`);
      setProg(r.data.progress);
      await refresh();
      if (r.data.progress?.status === "completed") loadList();
      if (data.passed) toast.success("Task validated");
    } catch (e) { toast.error(apiErr(e)); } finally { setValidating(false); }
  };

  return (
    <div className="fixed right-0 top-[40px] bottom-0 w-full sm:w-[380px] z-40 flex flex-col border-l border-[#414750] shadow-2xl bg-[#0f1b2d] text-slate-100" data-testid="aws-guided-panel">
      <div className="flex items-center gap-2 px-4 py-3 shrink-0 bg-[#161e2d] border-b border-[#414750]">
        <GraduationCap className="h-5 w-5 text-[#ff9900]" />
        <span className="font-semibold text-sm">Guided AWS Labs</span>
        <button className="ml-auto text-slate-400 hover:text-white" onClick={onClose} data-testid="aws-guided-close"><X className="h-4 w-4" /></button>
      </div>

      {!scn && (
        <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="aws-guided-picker">
          <p className="text-xs text-slate-400">Pick a production ticket. Work in this real console and validate as you go — no blind “Next”.</p>
          {scenarios.map((s) => (
            <button key={s.id} onClick={() => startScenario(s.id)} data-testid={`aws-guided-pick-${s.id}`}
              className="w-full text-left rounded-lg border border-[#414750] p-3 hover:border-[#ff9900]/60 transition-colors">
              <div className="flex items-center gap-2 text-[11px]">
                <span className={`font-semibold ${DIFF[s.difficulty]}`}>{s.difficulty}</span>
                <span className="font-mono text-slate-400 flex items-center gap-1"><Ticket className="h-3 w-3" />{s.ticket}</span>
                {s.status === "completed" && <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400 ml-auto" />}
              </div>
              <div className="font-medium text-sm mt-1">{s.title}</div>
              <div className="text-[11px] text-slate-400 mt-1 line-clamp-2">{s.business}</div>
            </button>
          ))}
        </div>
      )}

      {scn && (
        <>
          <div className="px-4 py-2.5 border-b border-[#414750] flex items-center gap-2 text-xs shrink-0">
            <span className="font-mono text-[#ff9900] flex items-center gap-1"><Ticket className="h-3.5 w-3.5" />{scn.ticket}</span>
            <span className="text-slate-400 truncate">{scn.title}</span>
            <button className="ml-auto text-slate-400 hover:text-white flex items-center gap-1" onClick={() => { setScn(null); setSid(null); setResult(null); }} data-testid="aws-guided-switch">
              <Minimize2 className="h-3.5 w-3.5" /> Labs
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4">
            <div className="flex flex-wrap gap-1.5 mb-3">
              {scn.tasks.map((t, i) => {
                const st = taskStatus(t.id);
                return <span key={t.id} className={`text-[10px] px-2 py-0.5 rounded-full border flex items-center gap-1 ${
                  st === "passed" ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/40"
                  : st === "active" ? "bg-[#ff9900]/15 text-[#ff9900] border-[#ff9900]/40"
                  : "bg-white/5 text-slate-400 border-[#414750]"}`}>
                  {st === "passed" ? <CheckCircle2 className="h-3 w-3" /> : i + 1}
                </span>;
              })}
            </div>

            {completed ? (
              <div className="rounded-lg border border-emerald-500/40 bg-emerald-500/10 p-4" data-testid="aws-guided-complete">
                <div className="text-center">
                  <Trophy className="h-7 w-7 text-emerald-400 mx-auto" />
                  <div className="font-semibold mt-2">Ticket resolved!</div>
                  <div className="text-[11px] text-slate-400 font-mono">{scn.ticket} · {scn.tasks.length}/{scn.tasks.length} tasks</div>
                </div>
                <ul className="mt-3 text-xs space-y-1">
                  {scn.outcomes.map((o, i) => <li key={i} className="flex items-start gap-1.5"><CheckCircle2 className="h-3 w-3 text-emerald-400 mt-0.5 shrink-0" /> {o}</li>)}
                </ul>
                {(scn.skills || []).length > 0 && (
                  <div className="mt-3" data-testid="aws-guided-skills">
                    <div className="text-[11px] uppercase tracking-wide text-slate-400 mb-1.5">Skills earned</div>
                    <div className="flex flex-wrap gap-1.5">
                      {scn.skills.map((sk) => <span key={sk} className="text-[11px] px-2 py-0.5 rounded-full bg-[#ff9900]/15 text-[#ff9900] border border-[#ff9900]/30">{sk}</span>)}
                    </div>
                  </div>
                )}
                {nextRec ? (
                  <div className="mt-4 rounded-md border border-[#414750] bg-white/5 p-3" data-testid="aws-guided-next-rec">
                    <div className="text-[11px] uppercase tracking-wide text-slate-400 mb-1">Recommended next</div>
                    <div className="flex items-center gap-2 text-[11px]">
                      <span className={`font-semibold ${DIFF[nextRec.difficulty]}`}>{nextRec.difficulty}</span>
                      <span className="font-mono text-slate-400 flex items-center gap-1"><Ticket className="h-3 w-3" />{nextRec.ticket}</span>
                    </div>
                    <div className="font-medium text-sm mt-0.5">{nextRec.title}</div>
                    <Button size="sm" className="w-full mt-2.5 bg-[#ff9900] hover:bg-[#ec7211] text-black font-semibold" onClick={() => { setResult(null); startScenario(nextRec.id); }} data-testid="aws-guided-start-next">
                      Start this lab <ArrowRight className="h-3.5 w-3.5 ml-1" />
                    </Button>
                  </div>
                ) : (
                  <div className="mt-4 text-center text-xs text-emerald-400 font-medium" data-testid="aws-guided-all-done">🎉 You've completed every guided AWS lab. Outstanding work!</div>
                )}
                <Button size="sm" variant="ghost" className="w-full mt-2 text-slate-400 hover:text-white" onClick={() => { setScn(null); setSid(null); loadList(); }} data-testid="aws-guided-back-list">Back to all labs</Button>
              </div>
            ) : task ? (
              <div data-testid="aws-guided-task">
                <div className="text-[11px] text-slate-400">Task {curIdx + 1} of {scn.tasks.length}</div>
                <div className="font-semibold text-sm mt-0.5">{task.title}</div>
                <p className="text-sm mt-1.5 text-slate-200">{task.instruction}</p>
                <div className="mt-2 rounded-md bg-white/5 p-2.5 text-xs text-slate-400"><span className="font-medium text-slate-200">Why: </span>{task.why}</div>

                {(task.steps || []).length > 0 && (
                  <div className="mt-3 rounded-lg border border-[#ff9900]/30 bg-[#ff9900]/10 p-3" data-testid="aws-guided-steps">
                    <div className="flex items-center gap-1.5 text-xs font-semibold text-[#ff9900] mb-2">
                      <ListChecks className="h-4 w-4" /> Steps to complete
                    </div>
                    <ol className="space-y-1.5">
                      {task.steps.map((s, i) => (
                        <li key={i} className="flex gap-2 text-xs leading-relaxed text-slate-200" data-testid={`aws-guided-step-${i}`}>
                          <span className="shrink-0 h-4 w-4 rounded-full bg-[#ff9900] text-black text-[10px] font-bold grid place-items-center mt-0.5">{i + 1}</span>
                          <span>{renderRich(s)}</span>
                        </li>
                      ))}
                    </ol>
                  </div>
                )}

                {task.nav && setView && (
                  <button className="mt-3 text-xs text-[#ff9900] hover:underline flex items-center gap-1" data-testid="aws-guided-goto" onClick={() => setView(task.nav)}>
                    Go to the relevant service <ChevronRight className="h-3.5 w-3.5" />
                  </button>
                )}

                <Button className="w-full mt-3 gap-2 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={validate} disabled={validating} data-testid="aws-guided-validate">
                  <ShieldCheck className="h-4 w-4" /> {validating ? "Checking…" : "Validate my work"}
                </Button>

                {result && (
                  <div className={`mt-3 rounded-md border p-3 ${result.passed ? "border-emerald-500/40 bg-emerald-500/10" : "border-rose-500/40 bg-rose-500/10"}`} data-testid="aws-guided-result">
                    <div className="flex items-center gap-1.5 text-sm font-semibold">
                      {result.passed ? <CheckCircle2 className="h-4 w-4 text-emerald-400" /> : <XCircle className="h-4 w-4 text-rose-400" />}
                      {result.passed ? "Task completed" : "Not yet"}
                    </div>
                    <p className="text-xs mt-1 text-slate-200">{result.message}</p>
                    {(result.expected || result.current) && (
                      <div className="mt-2 text-[11px] font-mono space-y-1">
                        {result.expected && <div className="rounded bg-emerald-500/15 px-2 py-1">Expected: {result.expected}</div>}
                        {result.current && <div className="rounded bg-rose-500/15 px-2 py-1">Current: {result.current}</div>}
                      </div>
                    )}
                    {result.hint && (
                      <div className="mt-2 flex items-start gap-1.5 text-xs bg-amber-500/15 text-amber-300 rounded-md p-2" data-testid="aws-guided-hint">
                        <Lightbulb className="h-3.5 w-3.5 mt-0.5 shrink-0" /><span><strong>Hint {result.hint_level}:</strong> {result.hint}</span>
                      </div>
                    )}
                    {result.passed && result.next_task && (
                      <Button size="sm" className="mt-3 w-full gap-1.5 bg-[#ff9900] hover:bg-[#ec7211] text-black font-semibold" onClick={() => setResult(null)} data-testid="aws-guided-next-task">
                        Next task <ArrowRight className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                )}
              </div>
            ) : null}
          </div>
        </>
      )}
    </div>
  );
}
